import type { UserPreferences } from '@/types/user';

export type ResolvedTheme = 'light' | 'dark' | 'night';

export function resolveTheme(prefs: UserPreferences, now: Date = new Date()): ResolvedTheme {
  if (prefs.theme !== 'auto') return prefs.theme;
  const h = now.getHours();
  const inNight =
    prefs.autoThemeStartNight <= prefs.autoThemeEndNight
      ? h >= prefs.autoThemeStartNight && h < prefs.autoThemeEndNight
      : h >= prefs.autoThemeStartNight || h < prefs.autoThemeEndNight;
  if (inNight) return 'night';
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

export function applyTheme(prefs: UserPreferences): ResolvedTheme {
  const theme = resolveTheme(prefs);
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  root.setAttribute('data-night-intensity', prefs.nightIntensity);
  try {
    localStorage.setItem('jnd:theme', prefs.theme);
    localStorage.setItem('jnd:night-intensity', prefs.nightIntensity);
  } catch {
    /* private mode */
  }
  // Type sizing
  root.style.setProperty('--font-scale-user', String(prefs.fontScale));
  root.style.setProperty('--lh-base', String(prefs.lineHeight));
  root.style.setProperty('--measure', `${prefs.measureCh}ch`);
  return theme;
}

export function startThemeAutoTicker(getPrefs: () => UserPreferences | undefined): () => void {
  const recompute = () => {
    const p = getPrefs();
    if (p) applyTheme(p);
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') recompute();
  };
  const interval = setInterval(recompute, 60_000);
  document.addEventListener('visibilitychange', onVisible);
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  mql.addEventListener('change', recompute);
  return () => {
    clearInterval(interval);
    document.removeEventListener('visibilitychange', onVisible);
    mql.removeEventListener('change', recompute);
  };
}
